import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Play,
  RotateCcw,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  SlidersHorizontal,
  Flame,
  Clock,
  Dumbbell,
  CheckCircle2,
  Calendar,
  Layers,
} from "lucide-react";
import {
  format,
  startOfWeek,
  addDays,
  addWeeks,
  subWeeks,
  isSameDay,
  isToday,
} from "date-fns";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { useActiveWorkout, normalizeExercises } from "@/lib/ActiveWorkoutContext";
import { Badge } from "@/components/ui/badge";
import FullScreenActiveWorkout from "@/components/workouts/FullScreenActiveWorkout";
import ExercisePreviewModal from "@/components/workouts/ExercisePreviewModal";
import PlanManagementModal from "@/components/workouts/PlanManagementModal";
import MembershipGate from "@/components/MembershipGate";
import { getWorkoutImage } from "@/utils/workoutImages";

const DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const difficultyBadges = {
  beginner: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  intermediate: "bg-sky-500/10 text-sky-400 border-sky-500/20",
  advanced: "bg-purple-500/10 text-purple-400 border-purple-500/20",
};

export default function Workouts() {
  const { user } = useAuth();
  const { active, startWorkout, resumeWorkout } = useActiveWorkout();

  // Current selected week anchor & selected date
  const [currentWeekStart, setCurrentWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [selectedDate, setSelectedDate] = useState(() => new Date());

  // Modal states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [manageModalOpen, setManageModalOpen] = useState(false);

  // 1. Fetch user's weekly schedule
  const { data: weeklyPlans = [], isLoading: weeklyLoading } = useQuery({
    queryKey: ["userWeeklyPlans", user?.id],
    queryFn: () => base44.entities.UserWeeklyWorkoutPlan.filter({ user_id: user?.id }),
    enabled: !!user,
  });
  const weeklyPlan = weeklyPlans[0] || null;

  // 2. Fetch workout plans & templates
  const { data: workouts = [], isLoading: workoutsLoading } = useQuery({
    queryKey: ["workouts"],
    queryFn: () => base44.entities.WorkoutPlan.list(),
  });

  const { data: templates = [] } = useQuery({
    queryKey: ["workoutTemplates"],
    queryFn: () => base44.entities.WorkoutTemplate.list(),
  });

  // 3. Fetch exercise catalogue for rich GIFs and instructions
  const { data: allExercises = [] } = useQuery({
    queryKey: ["all-exercises-catalogue"],
    queryFn: () => base44.entities.Exercises.list("name", 2500),
    staleTime: 1000 * 60 * 60,
  });

  // 4. Fetch user completions
  const { data: completions = [] } = useQuery({
    queryKey: ["workout-completions", user?.id],
    queryFn: () => base44.entities.WorkoutCompletion.filter({ user_id: user?.id }),
    enabled: !!user,
  });

  // Build the 7 days of the currently selected week
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const date = addDays(currentWeekStart, i);
      const dayKey = DAY_KEYS[i];
      const planId = weeklyPlan ? weeklyPlan[dayKey] : null;
      const dateStr = format(date, "yyyy-MM-dd");
      const isCompleted = completions.some(
        (c) => c.completed_date === dateStr || (c.workout_plan_id === planId && c.completed_date === dateStr)
      );

      return {
        date,
        dayKey,
        dayLabel: format(date, "EEE").toUpperCase(), // MON, TUE...
        dateNumber: format(date, "d"),
        planId,
        hasPlan: Boolean(planId),
        isCompleted,
        isToday: isToday(date),
        isSelected: isSameDay(date, selectedDate),
      };
    });
  }, [currentWeekStart, weeklyPlan, completions, selectedDate]);

  // Selected day's key & plan
  const selectedDayKey = format(selectedDate, "eeee").toLowerCase();
  const selectedPlanId = weeklyPlan ? weeklyPlan[selectedDayKey] : null;

  const selectedPlan = useMemo(() => {
    if (!selectedPlanId) return null;
    const fromWorkouts = workouts.find(
      (w) => w.id === selectedPlanId || w._id === selectedPlanId || String(w.id || w._id) === String(selectedPlanId)
    );
    if (fromWorkouts) return fromWorkouts;
    const fromTemplates = templates.find(
      (t) => t.id === selectedPlanId || t._id === selectedPlanId || String(t.id || t._id) === String(selectedPlanId)
    );
    return fromTemplates || null;
  }, [selectedPlanId, workouts, templates]);

  const exercises = useMemo(() => {
    return normalizeExercises(selectedPlan?.exercises);
  }, [selectedPlan]);
  const totalExercises = exercises.length;

  // First exercise GIF for cinematic hero background
  const heroGifUrl = useMemo(() => {
    if (!exercises.length) return null;
    const firstEx = exercises[0];
    const name = (firstEx.name || "").toLowerCase().trim();
    const clean = (s) => s.toLowerCase().replace(/[-_]/g, " ").replace(/[()]/g, "").trim();
    const cleanName = clean(name);
    const match = allExercises.find((e) => {
      const eName = (e.name || "").toLowerCase().trim();
      return eName === name || clean(eName) === cleanName || eName.includes(cleanName);
    });
    return firstEx?.gifUrl || firstEx?.gif_url || match?.gifUrl || match?.gif_url || null;
  }, [exercises, allExercises]);

  const workoutHeroImage = useMemo(
    () => getWorkoutImage(selectedPlan),
    [selectedPlan]
  );

  // Check if selected date is today
  const isSelectedToday = isToday(selectedDate);

  // Check if an active session is in progress for this workout
  const hasActiveSession = Boolean(
    active && (active.plan_id === selectedPlanId || active.plan_name === selectedPlan?.name)
  );

  // Start is only applicable for today's scheduled workout (or resuming an active session)
  const canStartOrResume = isSelectedToday || hasActiveSession;

  // Primary Action handler: Start or Resume Workout
  const handleStartOrResume = () => {
    if (!canStartOrResume) return;
    if (hasActiveSession) {
      resumeWorkout();
    } else if (selectedPlan) {
      startWorkout({
        ...selectedPlan,
        exercises,
      });
    }
  };

  // Explore exercises handler
  const handleExploreExercises = (startIndex = 0) => {
    setPreviewIndex(startIndex);
    setPreviewOpen(true);
  };

  // Week navigation
  const handlePrevWeek = () => {
    setCurrentWeekStart((prev) => subWeeks(prev, 1));
  };

  const handleNextWeek = () => {
    setCurrentWeekStart((prev) => addWeeks(prev, 1));
  };

  const weekRangeLabel = useMemo(() => {
    const end = addDays(currentWeekStart, 6);
    return `${format(currentWeekStart, "MMM d")} – ${format(end, "MMM d")}`;
  }, [currentWeekStart]);

  // If active fullscreen workout is open, render the full screen mode immediately
  if (active?.is_fullscreen) {
    return <FullScreenActiveWorkout allExercises={allExercises} />;
  }

  return (
    <MembershipGate>
      <div className="space-y-6 pb-20 select-none">
      {/* 1. PAGE HEADER */}
      <div className="flex items-center justify-between pt-1">
        <div>
          <span className="text-[10px] sm:text-[11px] font-bold tracking-widest text-sky-400 uppercase block">
            Your Training Program
          </span>
          <h1 className="text-2xl sm:text-3xl font-display font-black tracking-tight text-white uppercase">
            The Workouts.
          </h1>
        </div>

        {/* Small Plan Management Icon */}
        <button
          type="button"
          onClick={() => setManageModalOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 hover:bg-sky-500/20 border border-white/10 text-white hover:text-sky-300 font-bold text-xs transition active:scale-95 cursor-pointer shadow-sm"
          title="Training Management & Plan Library"
        >
          <SlidersHorizontal className="w-3.5 h-3.5 text-sky-400" />
          <span className="hidden sm:inline">Manage Plans</span>
          <span className="sm:hidden">Plans</span>
        </button>
      </div>

      {/* 2. COMPACT HORIZONTAL DAY SELECTOR */}
      <div className="space-y-2">
        <div className="flex items-center justify-between px-1">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-sky-400" />
            {weekRangeLabel}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handlePrevWeek}
              className="w-7 h-7 rounded-lg bg-[#101C2E] hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition"
              aria-label="Previous Week"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleNextWeek}
              className="w-7 h-7 rounded-lg bg-[#101C2E] hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition"
              aria-label="Next Week"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* 7-Day Selector Strip */}
        <div className="grid grid-cols-7 gap-1.5 sm:gap-2 bg-[#101C2E]/60 border border-white/10 rounded-2xl p-2">
          {weekDays.map((day) => {
            const isSelected = day.isSelected;
            return (
              <button
                key={day.dayKey}
                type="button"
                onClick={() => setSelectedDate(day.date)}
                className={`flex flex-col items-center justify-center py-2.5 rounded-xl transition-all relative ${
                  isSelected
                    ? "bg-gradient-to-b from-sky-500 to-blue-600 text-white font-bold shadow-[0_0_15px_rgba(14,165,233,0.4)] scale-[1.02]"
                    : "hover:bg-white/5 text-slate-400 hover:text-white"
                }`}
              >
                <span className="text-[10px] uppercase font-bold tracking-tight mb-0.5">
                  {day.dayLabel}
                </span>
                <span
                  className={`text-sm sm:text-base font-extrabold ${
                    isSelected ? "text-white" : day.isToday ? "text-sky-400" : "text-slate-300"
                  }`}
                >
                  {day.dateNumber}
                </span>

                {/* Scheduled Workout Indicator */}
                <div className="h-1.5 flex items-center gap-1 mt-1">
                  {day.isCompleted ? (
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
                  ) : day.hasPlan ? (
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        isSelected ? "bg-white" : "bg-sky-400"
                      }`}
                    />
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. FEATURED WORKOUT HERO */}
      {selectedPlan ? (
        <div className="relative rounded-3xl overflow-hidden bg-[#0A1220] border border-white/15 shadow-2xl">
          {/* Background Vignette with Image / GIF */}
          {(workoutHeroImage || heroGifUrl) && (
            <div className="absolute inset-0 pointer-events-none opacity-30 overflow-hidden">
              <img
                src={workoutHeroImage || heroGifUrl}
                alt="Workout preview"
                className="w-full h-full object-cover filter blur-sm scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1220] via-[#0A1220]/80 to-[#0A1220]/40" />
            </div>
          )}

          <div className="relative p-5 sm:p-7 space-y-4">
            {/* Badges row */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Type Badge */}
              <Badge
                variant="outline"
                className="bg-purple-500/15 border-purple-500/30 text-purple-300 text-xs font-bold uppercase tracking-wider px-2.5 py-0.5"
              >
                {selectedPlan.goal === "hypertrophy"
                  ? "Hypertrophy"
                  : selectedPlan.goal === "strength"
                  ? "Strength"
                  : selectedPlan.goal || "Hypertrophy"}
              </Badge>

              {selectedPlan.difficulty && (
                <Badge
                  variant="outline"
                  className={`text-xs font-semibold uppercase ${
                    difficultyBadges[selectedPlan.difficulty] || ""
                  }`}
                >
                  {selectedPlan.difficulty}
                </Badge>
              )}

              {hasActiveSession && (
                <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 text-xs font-bold animate-pulse">
                  Session In Progress
                </Badge>
              )}

              {!isSelectedToday && !hasActiveSession && (
                <Badge
                  variant="outline"
                  className="bg-white/5 border-white/10 text-slate-400 text-xs font-semibold capitalize"
                >
                  Scheduled for {format(selectedDate, "EEEE")}
                </Badge>
              )}
            </div>

            {/* Title & Description */}
            <div className="space-y-1.5">
              <h2 className="text-2xl sm:text-3xl font-display font-black text-white uppercase tracking-tight">
                {selectedPlan.name}
              </h2>
              {selectedPlan.description && (
                <p className="text-xs sm:text-sm text-slate-300 line-clamp-2 max-w-xl">
                  {selectedPlan.description}
                </p>
              )}
            </div>

            {/* Metadata chips: Exercises count, Duration */}
            <div className="flex items-center gap-4 text-xs text-slate-300 font-medium">
              <div className="flex items-center gap-1.5">
                <Dumbbell className="w-4 h-4 text-sky-400" />
                <span>{totalExercises} Exercises</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4 text-sky-400" />
                <span>{selectedPlan.estimated_minutes || 45} Min</span>
              </div>
            </div>

            {/* Primary Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
              {canStartOrResume && (
                <button
                  type="button"
                  onClick={handleStartOrResume}
                  className="flex-1 py-3.5 px-6 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white font-extrabold text-sm tracking-wide shadow-[0_0_25px_rgba(14,165,233,0.45)] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                >
                  {hasActiveSession ? (
                    <>
                      <RotateCcw className="w-4 h-4" /> RESUME WORKOUT
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 fill-white" /> START WORKOUT
                    </>
                  )}
                </button>
              )}

              <button
                type="button"
                onClick={() => handleExploreExercises(0)}
                className={`py-3.5 px-5 rounded-xl bg-white/10 hover:bg-white/15 text-white/90 font-bold text-xs border border-white/15 flex items-center justify-center gap-2 active:scale-95 transition ${
                  !canStartOrResume ? "flex-1 w-full text-sm py-3.5" : ""
                }`}
              >
                <Layers className="w-4 h-4 text-sky-400" /> EXPLORE EXERCISES
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* REST & RECOVERY DAY STATE */
        <div className="rounded-3xl bg-[#0A1220] border border-white/10 p-8 text-center space-y-4 shadow-xl">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-sky-500/10 border border-sky-400/20 flex items-center justify-center text-sky-400 shadow-[0_0_20px_rgba(14,165,233,0.2)]">
            <Sparkles className="w-7 h-7" />
          </div>

          <div className="space-y-1">
            <p className="text-xs font-bold tracking-widest text-sky-400 uppercase">
              Schedule Status
            </p>
            <h2 className="text-2xl font-display font-black text-white uppercase tracking-tight">
              Rest & Recovery
            </h2>
            <p className="text-xs sm:text-sm text-slate-400 max-w-sm mx-auto leading-relaxed">
              Active recovery day. Hydrate, stretch, and allow your muscle fibers to rebuild.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setManageModalOpen(true)}
            className="py-3 px-6 rounded-xl bg-[#101C2E] hover:bg-[#16253D] border border-white/15 text-xs font-bold text-sky-300 hover:text-white transition active:scale-95 shadow-sm inline-flex items-center gap-2"
          >
            <SlidersHorizontal className="w-3.5 h-3.5 text-sky-400" /> Assign a Workout to this Day
          </button>
        </div>
      )}

      {/* 4. MINIMALIST EXERCISE LINEUP */}
      {selectedPlan && exercises.length > 0 && (
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Exercise Lineup ({totalExercises})
            </h3>
            <span className="text-[11px] text-sky-400/80 font-medium">
              Tap to preview instructions
            </span>
          </div>

          <div className="bg-[#101C2E] border border-white/10 rounded-2xl divide-y divide-white/5 overflow-hidden">
            {exercises.map((ex, index) => {
              const fullEx = allExercises.find(
                (e) => (e.name || "").toLowerCase().trim() === (ex.name || "").toLowerCase().trim()
              );
              const targetMuscle =
                (Array.isArray(ex.targetMuscles) && ex.targetMuscles[0]) ||
                (Array.isArray(fullEx?.targetMuscles) && fullEx.targetMuscles[0]) ||
                ex.muscles_primary ||
                fullEx?.muscles_primary ||
                "General";

              return (
                <div
                  key={index}
                  onClick={() => handleExploreExercises(index)}
                  className="flex items-center justify-between p-3.5 hover:bg-white/5 cursor-pointer transition active:bg-white/10"
                >
                  <div className="flex items-center gap-3.5 min-w-0">
                    <span className="text-xs font-extrabold text-sky-400/80 font-mono w-5">
                      #{index + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-white capitalize truncate">
                        {ex.name}
                      </p>
                      <p className="text-[11px] text-slate-400 capitalize">
                        {targetMuscle}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-sky-300 font-semibold font-mono">
                      {ex.sets || 3} × {ex.reps || "8-12"}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 5. EXERCISE PREVIEW MODAL */}
      <ExercisePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        exercises={exercises}
        initialIndex={previewIndex}
        allExercises={allExercises}
        workoutName={selectedPlan?.name || "Workout"}
        onStartWorkout={canStartOrResume ? handleStartOrResume : undefined}
      />

      {/* 6. RELOCATED PLAN MANAGEMENT MODAL */}
        <PlanManagementModal
          open={manageModalOpen}
          onClose={() => setManageModalOpen(false)}
        />
      </div>
    </MembershipGate>
  );
}